/**
 * Incremental RFC-4180 tokenizer.
 *
 * Feed it decoded text in arbitrary chunks (a chunk boundary may fall anywhere — inside a quoted field,
 * between the two quotes of an escaped `""`, between a CR and its LF) and it emits complete records.
 * It never holds more than the current record, so memory is bounded by the largest single record.
 *
 * Dialect (RFC 4180 plus the leniency real-world spreadsheets need):
 *   - fields may be wrapped in `"`; inside, `""` is a literal quote and CR / LF are data;
 *   - records end at CRLF, LF or a lone CR;
 *   - a `"` in the middle of an unquoted field, or text after a closing quote, is kept literally;
 *   - an unterminated quote at end of input is an ERROR (it is never silently auto-closed).
 */

import { type CsvDelimiter, CsvParseError, type CsvRow } from "./types";

const QUOTE = 34;
const LF = 10;
const CR = 13;

export interface TokenizerOptions {
  delimiter: CsvDelimiter;
  skipEmptyLines: boolean;
  maxFieldLength: number;
}

export class Tokenizer {
  private readonly splitter: RegExp;
  private readonly skipEmptyLines: boolean;
  private readonly maxFieldLength: number;

  private inQuotes = false;
  /** A `"` was the last char of the previous chunk while quoted: escaped quote or close? Decided by the next char. */
  private pendingQuote = false;
  /** The current field was opened with a quote (so `""` is an empty value, not a blank line). */
  private quoted = false;
  /** The previous char was a CR that ended a record; swallow an immediately following LF. */
  private skipLF = false;
  /** Line counting inside quoted text: the last counted char was a CR (so a following LF is the same break). */
  private prevCR = false;

  private field = "";
  private row: string[] = [];
  private line = 1;
  private recordLine = 1;
  private index = 1;

  rowsYielded = 0;
  blankLinesSkipped = 0;

  constructor(options: TokenizerOptions) {
    const delimiterClass = options.delimiter === "\t" ? "\\t" : options.delimiter;
    this.splitter = new RegExp(`[${delimiterClass}\\n\\r"]`, "g");
    this.skipEmptyLines = options.skipEmptyLines;
    this.maxFieldLength = options.maxFieldLength;
  }

  /** Consume a chunk of decoded text, appending any completed records to `out`. */
  push(chunk: string, out: CsvRow[]): void {
    const n = chunk.length;
    if (n === 0) return;
    let i = 0;

    if (this.skipLF) {
      this.skipLF = false;
      if (chunk.charCodeAt(0) === LF) i = 1;
    }
    if (this.pendingQuote) {
      this.pendingQuote = false;
      if (i < n && chunk.charCodeAt(i) === QUOTE) {
        this.field += '"';
        this.guardLength();
        i += 1;
      } else {
        this.inQuotes = false;
      }
    }

    const splitter = this.splitter;
    while (i < n) {
      if (this.inQuotes) {
        const q = chunk.indexOf('"', i);
        if (q === -1) {
          this.appendQuoted(chunk.slice(i));
          return;
        }
        if (q > i) this.appendQuoted(chunk.slice(i, q));
        if (q + 1 >= n) {
          this.pendingQuote = true;
          return;
        }
        if (chunk.charCodeAt(q + 1) === QUOTE) {
          this.field += '"';
          this.guardLength();
          i = q + 2;
        } else {
          this.inQuotes = false;
          i = q + 1;
        }
        continue;
      }

      this.prevCR = false;
      splitter.lastIndex = i;
      const match = splitter.exec(chunk);
      if (match === null) {
        this.field += chunk.slice(i);
        this.guardLength();
        return;
      }
      const p = match.index;
      if (p > i) {
        this.field += chunk.slice(i, p);
        this.guardLength();
      }
      const c = chunk.charCodeAt(p);
      if (c === QUOTE) {
        if (!this.quoted && this.field.length === 0) {
          this.inQuotes = true;
          this.quoted = true;
        } else {
          this.field += '"';
        }
        i = p + 1;
      } else if (c === LF) {
        this.endRecord(out);
        this.line += 1;
        this.recordLine = this.line;
        i = p + 1;
      } else if (c === CR) {
        this.endRecord(out);
        this.line += 1;
        this.recordLine = this.line;
        if (p + 1 < n) {
          i = chunk.charCodeAt(p + 1) === LF ? p + 2 : p + 1;
        } else {
          this.skipLF = true;
          i = p + 1;
        }
      } else {
        this.endField();
        i = p + 1;
      }
    }
  }

  /** Signal end of input. Flushes the last record; throws on an unterminated quoted field. */
  finish(out: CsvRow[]): void {
    if (this.pendingQuote) {
      this.pendingQuote = false;
      this.inQuotes = false;
    }
    if (this.inQuotes) {
      throw new CsvParseError(
        "unterminated_quote",
        `The file ends inside a quoted field that started on line ${this.recordLine}. A closing double quote is missing.`,
        this.recordLine
      );
    }
    if (this.row.length > 0 || this.field.length > 0 || this.quoted) {
      this.endRecord(out);
    }
  }

  private guardLength(): void {
    if (this.field.length > this.maxFieldLength) {
      throw new CsvParseError(
        "field_too_large",
        `A single cell starting on line ${this.recordLine} is longer than ${this.maxFieldLength.toLocaleString("en-US")} characters. The file is probably missing a closing double quote.`,
        this.recordLine
      );
    }
  }

  private appendQuoted(text: string): void {
    this.field += text;
    this.guardLength();
    if (text.indexOf("\n") === -1 && text.indexOf("\r") === -1) {
      this.prevCR = false;
      return;
    }
    for (let k = 0; k < text.length; k++) {
      const c = text.charCodeAt(k);
      if (c === LF) {
        if (!this.prevCR) this.line += 1;
        this.prevCR = false;
      } else if (c === CR) {
        this.line += 1;
        this.prevCR = true;
      } else {
        this.prevCR = false;
      }
    }
  }

  private endField(): void {
    this.row.push(this.field);
    this.field = "";
    this.quoted = false;
  }

  private endRecord(out: CsvRow[]): void {
    const hasContent = this.row.length > 0 || this.field.length > 0 || this.quoted;
    const index = this.index++;
    if (!hasContent) {
      if (this.skipEmptyLines) {
        this.blankLinesSkipped += 1;
      } else {
        out.push({ index, line: this.recordLine, cells: [""] });
        this.rowsYielded += 1;
      }
      return;
    }
    this.endField();
    out.push({ index, line: this.recordLine, cells: this.row });
    this.row = [];
    this.rowsYielded += 1;
  }
}

/**
 * Guess the delimiter from the start of a file. Counts unquoted candidates per line over up to 10 complete
 * lines and prefers the one with a consistent, non-zero count. Falls back to a comma.
 */
export function detectDelimiter(sample: string, complete: boolean): CsvDelimiter {
  const candidates: CsvDelimiter[] = [",", ";", "\t", "|"];
  let best: CsvDelimiter = ",";
  let bestScore = 0;

  for (const candidate of candidates) {
    const counts = fieldCounts(sample, candidate, complete);
    if (counts.length === 0) continue;
    const tally = new Map<number, number>();
    for (const count of counts) tally.set(count, (tally.get(count) ?? 0) + 1);
    let modeCount = 0;
    let modeLines = 0;
    for (const [count, lines] of tally) {
      if (lines > modeLines || (lines === modeLines && count > modeCount)) {
        modeCount = count;
        modeLines = lines;
      }
    }
    if (modeCount < 2) continue;
    const score = (modeLines / counts.length) * 1000 + Math.min(modeCount, 100);
    if (score > bestScore) {
      best = candidate;
      bestScore = score;
    }
  }
  return best;
}

function fieldCounts(sample: string, delimiter: string, complete: boolean): number[] {
  const counts: number[] = [];
  let inQuotes = false;
  let fields = 1;
  let lineHasContent = false;
  const limit = 10;

  for (let i = 0; i < sample.length && counts.length < limit; i++) {
    const ch = sample[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      lineHasContent = true;
    } else if (!inQuotes && ch === delimiter) {
      fields += 1;
      lineHasContent = true;
    } else if (!inQuotes && (ch === "\n" || ch === "\r")) {
      if (ch === "\r" && sample[i + 1] === "\n") i += 1;
      if (lineHasContent) counts.push(fields);
      fields = 1;
      lineHasContent = false;
    } else {
      lineHasContent = true;
    }
  }
  // The sample may end mid-line (a truncated first chunk). Only count the tail when it is the whole input,
  // or when it is the only line there is.
  if (lineHasContent && counts.length < limit && (complete || counts.length === 0)) counts.push(fields);
  return counts;
}
